import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ListOrdered, Lock, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CHIPS = [["1H", 100], ["5H", 500], ["10H", 1000], ["25H", 2500], ["50H", 5000], ["1L", 100000], ["2L", 200000], ["5L", 500000], ["10L", 1000000], ["25L", 2500000]];
const LAGAI = "bg-[#72bbef] text-black";
const KHAI = "bg-[#faa9ba] text-black";

const Cell = ({ val, sub, cls, off, onClick, testid }) => (
  <button data-testid={testid} disabled={off || val == null} onClick={onClick}
    className={`flex h-12 flex-col items-center justify-center rounded font-bold transition-transform active:scale-95 disabled:cursor-not-allowed ${cls} ${off ? "opacity-40" : "hover:brightness-110"}`}>
    <span className="text-base leading-none">{val ?? "-"}</span>{sub != null && <span className="mt-0.5 text-[10px] font-semibold leading-none">{sub}</span>}
  </button>
);

const Suspended = () => <div className="absolute inset-0 grid place-items-center rounded bg-black/70 text-xs font-bold uppercase tracking-wider text-cr-danger"><span className="flex items-center gap-1"><Lock className="h-3 w-3" />Suspended</span></div>;

const BetSlip = ({ slip, onClose, onPlaced, limits, live, off }) => {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const { refreshWallet } = useAuth();
  useEffect(() => { setAmount(""); }, [slip.key, slip.side]);
  const rate = live === undefined ? slip.rate : live;
  const [lo, hi] = limits[slip.market];
  const a = Number(amount) || 0;
  const win = slip.market === "odds" ? (slip.side === "lagai" ? Math.floor((a * rate) / 100) : Math.floor((a * 100) / rate)) : Math.floor(a * rate);
  const place = async () => {
    setBusy(true);
    try {
      await api.post(`/exchange/${slip.fid}/bets`, { market: slip.market, key: slip.key, side: slip.side, rate, line: slip.line ?? null, amount: a });
      toast.success("Bet placed"); refreshWallet(); onPlaced();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const tone = slip.side === "lagai" || slip.side === "yes" ? "bg-[#72bbef]/15 border-[#72bbef]" : "bg-[#faa9ba]/15 border-[#faa9ba]";
  return (
    <div data-testid="bet-slip" className={`mt-2 rounded-lg border p-3 ${tone}`}>
      <div className="flex items-center justify-between text-sm">
        <p className="font-semibold">{slip.label} · <span className="uppercase">{slip.side}</span> @ <b data-testid="bet-slip-rate">{rate == null ? "—" : slip.line != null ? `${slip.line} / ${rate}` : rate}</b></p>
        <div className="flex items-center gap-3">
          <button aria-label="Close bet slip" data-testid="bet-slip-close" onClick={() => onClose(false)}><X className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-5 gap-2 sm:grid-cols-10">
        {CHIPS.filter(([, v]) => v <= hi).map(([l, v]) => (
          <button key={l} data-testid={`chip-${l}`} onClick={() => setAmount(String(v))} className={`rounded py-2 text-xs font-bold ${a === v ? "bg-cr-gold text-black" : "bg-cr-elevated hover:bg-cr-gold/20"}`}>{l}</button>))}
      </div>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <input data-testid="bet-amount-input" autoFocus inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))} placeholder={`Amount (${lo} - ${hi.toLocaleString("en-IN")})`} className="input-cr flex-1" />
        <button data-testid="bet-place-btn" disabled={busy || off || rate == null || a < lo || a > hi} onClick={place} className="btn-gold px-6 py-3 normal-case">{off || rate == null ? "Suspended" : busy ? "Placing…" : `Done · win ${win.toLocaleString("en-IN")}`}</button>
      </div>
    </div>
  );
};

const PosText = ({ v }) => (v ? <span className={`text-xs font-bold ${v > 0 ? "text-emerald-400" : "text-cr-danger"}`}>{v > 0 ? "+" : ""}{v.toLocaleString("en-IN")}</span> : null);

const OddsTable = ({ d, open, slip, done, close }) => (
  <div data-testid="match-odds-market" className="card-cr overflow-hidden">
    {d.hold && <div data-testid="exchange-hold-banner" className="animate-pulse bg-red-600 px-4 py-2 text-center text-sm font-black uppercase tracking-wider text-white">{d.hold.reason === "Ball Running" ? "Ball running · market suspended" : `${d.hold.reason}! Market suspended`} · reopens in {d.hold.seconds}s</div>}
    {d.promo?.cashback_pct > 0 && <div data-testid="exchange-cashback-banner" className="bg-emerald-600 px-4 py-1.5 text-center text-xs font-bold text-white">{d.promo.cashback_pct}% cashback on every lost bet</div>}
    <div className="flex items-center justify-between bg-cr-gold px-4 py-2 text-sm font-black uppercase text-black"><span>Market : Match Odds</span><span className="text-[11px] font-semibold normal-case">{d.match.ext && d.match.is_live ? `Score updated ${Math.round((d.match.data_age || 0) / 60)} min ago` : ""}</span></div>
    <div className="grid grid-cols-[1fr_64px_64px_64px] items-center gap-1.5 bg-black/30 px-3 py-2 text-center text-[11px] font-bold uppercase sm:grid-cols-[1fr_110px_110px_110px]">
      <span className="text-left normal-case text-sky-300">Min : {d.limits.odds[0]} | Max : {d.limits.odds[1].toLocaleString("en-IN")}</span>
      <span className="rounded bg-[#72bbef] py-1 text-black">Lagai</span><span className="rounded bg-[#faa9ba] py-1 text-black">Khai</span><span className="text-cr-muted">Position</span></div>
    {d.odds.map((r) => (
      <div key={r.side} className="border-t border-cr-line px-3 py-1.5">
        <div className="grid grid-cols-[1fr_64px_64px_64px] items-center gap-1.5 sm:grid-cols-[1fr_110px_110px_110px]">
          <p className="truncate text-sm font-semibold">{r.name}{r.boost > 0 && <span data-testid={`odds-boost-${r.side}`} className="ml-2 rounded bg-emerald-500 px-1.5 py-0.5 text-[10px] font-black text-black">BOOST +{r.boost}%</span>}</p>
          <div className="relative col-span-2 grid grid-cols-2 gap-1.5">
            <Cell testid={`odds-lagai-${r.side}`} val={r.lagai} cls={LAGAI} off={r.suspended} onClick={() => open({ market: "odds", key: r.side, side: "lagai", rate: r.lagai, label: r.name })} />
            <Cell testid={`odds-khai-${r.side}`} val={r.khai} cls={KHAI} off={r.suspended} onClick={() => open({ market: "odds", key: r.side, side: "khai", rate: r.khai, label: r.name })} />
            {r.suspended && <Suspended />}
          </div>
          <span data-testid={`odds-position-${r.side}`} className="text-center">{d.position?.[r.side] ? <PosText v={d.position[r.side]} /> : <span className="text-xs text-cr-muted">0.0</span>}</span>
        </div>
        {slip?.market === "odds" && slip.key === r.side && <BetSlip slip={slip} live={slip.side === "lagai" ? r.lagai : r.khai} off={r.suspended} limits={d.limits} onClose={close} onPlaced={done} />}
      </div>))}
  </div>
);

const ladderRows = (bets) => {
  const lines = bets.map((b) => b.line);
  const lo = Math.max(0, Math.min(...lines) - 3), hi = Math.max(...lines) + 3;
  return Array.from({ length: hi - lo + 1 }, (_, k) => {
    const runs = lo + k;
    return [runs, bets.reduce((s, b) => s + (((b.side === "yes") === (runs >= b.line)) ? b.win : -b.amount), 0)];
  });
};

const Ladder = ({ s, bets, onClose }) => (
  <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" onClick={onClose}>
    <div data-testid="session-ladder" className="card-cr w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between bg-cr-gold px-4 py-2 text-sm font-black text-black"><span>Book · {s.name}</span><button aria-label="Close book" data-testid="session-ladder-close" onClick={onClose}><X className="h-4 w-4" /></button></div>
      {bets.length === 0 ? <p className="p-4 text-sm text-cr-muted">You have no open bets on this session.</p> : (
        <div className="max-h-80 overflow-y-auto">{ladderRows(bets).map(([r, v]) => (
          <p key={r} className="flex justify-between border-t border-cr-line px-4 py-1.5 text-sm"><span>{r} runs</span><PosText v={v} />{!v && <span className="text-xs">0</span>}</p>))}</div>)}
    </div>
  </div>
);

const SessionTable = ({ d, open, slip, done, close }) => {
  const [book, setBook] = useState(null);
  const mine = (key) => (d.my_bets || []).filter((b) => b.market === "session" && b.key === key && b.status === "pending");
  return (
    <div data-testid="session-market" className="card-cr overflow-hidden">
      <div className="bg-cr-gold px-4 py-2 text-sm font-black uppercase text-black">Session</div>
      <div className="grid grid-cols-[1fr_52px_44px_52px_44px_32px] items-center gap-1 bg-black/30 px-3 py-2 text-center text-[11px] font-bold uppercase sm:grid-cols-[1fr_100px_70px_100px_70px_44px]">
        <span className="text-left normal-case text-sky-300">Min : {d.limits.session[0]} | Max : {d.limits.session[1].toLocaleString("en-IN")}</span>
        <span className="rounded bg-[#faa9ba] py-1 text-black">No</span><span className="text-cr-muted">Rate</span><span className="rounded bg-[#72bbef] py-1 text-black">Yes</span><span className="text-cr-muted">Rate</span><span /></div>
      {d.sessions.length === 0 && <p className="border-t border-cr-line px-4 py-4 text-sm text-cr-muted">{d.match.is_live ? (d.format ? "Sessions will open shortly." : "No session markets for multi-day matches.") : "Sessions open when the match is live."}</p>}
      {d.sessions.map((s) => (
        <div key={s.key} className="border-t border-cr-line px-3 py-1.5">
          <div className="grid grid-cols-[1fr_52px_44px_52px_44px_32px] items-center gap-1 sm:grid-cols-[1fr_100px_70px_100px_70px_44px]">
            <p className="line-clamp-2 text-xs font-semibold leading-tight sm:text-sm">{s.name}</p>
            <div className="relative col-span-4 grid grid-cols-[52px_44px_52px_44px] items-center gap-1 text-center sm:grid-cols-[100px_70px_100px_70px]">
              <Cell testid={`session-no-${s.key}`} val={s.no} cls={KHAI} off={s.suspended} onClick={() => open({ market: "session", key: s.key, side: "no", rate: s.no_rate, line: s.no, label: s.name })} />
              <span className="text-xs font-semibold">{s.no_rate.toFixed(2)}</span>
              <Cell testid={`session-yes-${s.key}`} val={s.yes} cls={LAGAI} off={s.suspended} onClick={() => open({ market: "session", key: s.key, side: "yes", rate: s.yes_rate, line: s.yes, label: s.name })} />
              <span className="text-xs font-semibold">{s.yes_rate.toFixed(2)}</span>
              {s.suspended && <Suspended />}
            </div>
            <button aria-label="Session book" data-testid={`session-book-${s.key}`} onClick={() => setBook(s)} className="grid h-9 place-items-center rounded hover:bg-white/10"><ListOrdered className="h-4 w-4" /></button>
          </div>
          {slip?.market === "session" && slip.key === s.key && <BetSlip slip={{ ...slip, line: slip.side === "no" ? s.no : s.yes }} live={slip.side === "no" ? s.no_rate : s.yes_rate} off={s.suspended} limits={d.limits} onClose={close} onPlaced={done} />}
        </div>))}
      {book && <Ladder s={book} bets={mine(book.key)} onClose={() => setBook(null)} />}
    </div>
  );
};

const BREAK_RE = /stump|lunch|tea|innings break|drinks|rain|bad light|delay|stops play|break/i;
const ballCls = (b) => (b === "W" ? "bg-cr-danger text-white" : b === "6" ? "bg-cr-gold text-black" : b === "4" ? "bg-emerald-500 text-black" : b === "wd" || b === "nb" ? "bg-sky-500/40" : "bg-white/10");

const ScoreBox = ({ d, onRefresh }) => {
  const s = d.score;
  if (!s || !d.match.is_live) return null;
  const onBreak = BREAK_RE.test(d.match.status || "");
  const state = onBreak ? "Break" : s.ball_state || "-";
  const [runs, balls] = s.need || [];
  return (
    <div data-testid="exchange-scorebox" className="grid grid-cols-[minmax(0,1fr)_68px_40px] gap-1.5 sm:grid-cols-[minmax(0,1fr)_200px_80px]">
      <div className="card-cr divide-y divide-cr-line break-words text-center text-xs font-semibold sm:text-sm">
        {s.innings.map((i) => <p key={i.team} className="py-1.5">{i.team} - {i.runs}/{i.wkts} ({i.overs}) Ovs</p>)}
        <p className="py-1.5">CRR :- {s.crr}{s.rrr != null && <>  RRR :- {s.rrr}</>}</p>
        {runs > 0 && balls > 0 && <p data-testid="exchange-need" className="py-1.5 text-cr-gold">{s.innings[s.innings.length - 1].team} Need {runs} Runs In {balls} Balls To Win</p>}
        {onBreak && <p data-testid="exchange-break" className="bg-amber-500 py-1.5 font-black uppercase text-black">{d.match.status}</p>}
        <div data-testid="exchange-last6" className="flex flex-wrap items-center justify-center gap-1 py-1.5"><span className="mr-1 whitespace-nowrap text-xs">Last 6 Ball</span>
          {Array.from({ length: 6 }, (_, k) => s.last6[k]).map((b, k) => <span key={k} className={`grid h-6 min-w-6 place-items-center rounded-full px-1 text-[10px] font-black sm:h-7 sm:min-w-7 ${b ? ballCls(b) : "bg-white/5 text-cr-muted"}`}>{b ?? "-"}</span>)}</div>
      </div>
      <div data-testid="exchange-ball-state" className={`card-cr grid place-items-center break-words px-1 text-center font-display text-sm font-black leading-tight sm:text-2xl ${state === "W" || state === "Wicket" ? "text-cr-danger" : state === "Break" ? "text-amber-400" : "text-cr-gold"}`}>{state}</div>
      <button aria-label="Refresh" data-testid="exchange-refresh" onClick={onRefresh} className="card-cr grid place-items-center hover:bg-white/5"><RefreshCw className="h-5 w-5" /></button>
    </div>
  );
};

const MyBets = ({ bets }) => bets?.length > 0 && (
  <div data-testid="my-exchange-bets" className="card-cr overflow-x-auto">
    <p className="px-4 pt-3 font-semibold">My bets</p>
    <table className="w-full min-w-[560px] text-sm"><thead className="text-left text-xs uppercase text-cr-muted"><tr>{["Market", "Side", "Rate", "Amount", "Win", "Status"].map((h) => <th key={h} className="px-4 py-2">{h}</th>)}</tr></thead>
      <tbody>{bets.map((b) => (
        <tr key={b.id} data-testid={`xbet-${b.id}`} className={`border-t border-cr-line ${b.side === "lagai" || b.side === "yes" ? "bg-[#72bbef]/5" : "bg-[#faa9ba]/5"}`}>
          <td className="px-4 py-2">{b.name}</td><td className="px-4 py-2 font-semibold uppercase">{b.side}</td><td className="px-4 py-2">{b.line != null ? `${b.line} / ${b.rate}` : b.rate}</td>
          <td className="px-4 py-2">{b.amount.toLocaleString("en-IN")}</td><td className="px-4 py-2 text-cr-gold">{b.win.toLocaleString("en-IN")}</td>
          <td className={`px-4 py-2 font-semibold capitalize ${b.status === "won" ? "text-emerald-400" : b.status === "lost" ? "text-cr-danger" : ""}`}>{b.status}
            {b.corrections?.length > 0 && <span data-testid={`my-bet-corrected-${b.id}`} title={b.corrections[b.corrections.length - 1].reason} className="ml-1 rounded bg-cr-gold/20 px-1.5 py-0.5 text-[10px] normal-case text-cr-gold">Corrected: {b.corrections[b.corrections.length - 1].reason}</span>}</td></tr>))}</tbody></table>
  </div>
);

export default function ExchangeBoard({ fid }) {
  const { user, refreshWallet } = useAuth();
  const [d, setD] = useState(null);
  const [slip, setSlip] = useState(null);
  const load = useCallback(() => api.get(`/exchange/${fid}`).then((r) => setD(r.data)).catch(() => {}), [fid]);
  const prevBets = useRef(null);
  useEffect(() => {
    const now = Object.fromEntries((d?.my_bets || []).map((b) => [b.id, b]));
    const prev = prevBets.current;
    if (prev) {
      Object.values(now).forEach((b) => {
        if (prev[b.id]?.status === "pending" && b.status !== "pending") {
          if (b.status === "won") toast.success(`Bet WON · ${b.name}: +${(b.amount + b.win).toLocaleString("en-IN")} coins`);
          else if (b.status === "lost") toast.error(`Bet LOST · ${b.name}: -${b.amount.toLocaleString("en-IN")} coins`);
          else toast.info(`Bet VOID · ${b.name}: ${b.amount} coins returned`);
          refreshWallet();
        }
      });
    }
    if (d) prevBets.current = now;
  }, [d]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    if (!d?.hold) return undefined;
    const t = setTimeout(load, d.hold.seconds * 1000 + 200);
    return () => clearTimeout(t);
  }, [d?.hold?.reason, d?.hold?.seconds, load]); // eslint-disable-line react-hooks/exhaustive-deps
  const open = (s) => {
    if (!user) return toast.error("Please log in to place a bet");
    if (user.role !== "user") return toast.error("Only player accounts can bet");
    setSlip({ ...s, fid, id: Date.now() });
  };
  const close = useCallback(() => { setSlip(null); load(); }, [load]);
  const done = () => { setSlip(null); load(); };
  if (!d) return null;
  return (
    <section data-testid="exchange-board" className="mt-8 space-y-4">
      {!user && <p className="text-sm text-cr-muted"><Link to="/login" className="font-semibold text-cr-gold">Log in</Link> to place bets.</p>}
      {d.counts && <div className="flex gap-3 text-xs"><span className="rounded bg-cr-elevated px-3 py-1.5">Match bets · {d.counts.odds}</span><span className="rounded bg-cr-elevated px-3 py-1.5">Session bets · {d.counts.session}</span></div>}
      <ScoreBox d={d} onRefresh={load} />
      <OddsTable d={d} open={open} slip={slip} done={done} close={close} />
      <SessionTable d={d} open={open} slip={slip} done={done} close={close} />
      <MyBets bets={d.my_bets} />
    </section>
  );
}
